import * as XLSX from 'xlsx';
import { z } from 'zod';
import {
  DataSyncExcelDownloadedIdsSchema,
  DataSyncExcelSheetSchema,
  StartExcelDataSyncRequestSchema,
  type DataSyncExcelDownloadedIds,
  type DataSyncExcelSheet,
  type StartExcelDataSyncRequest
} from '@bliks-io/contracts';
import i18next from '@/lib/i18n';
import { ExcelHeaderMismatchError } from '@bliks-io/ui';
import {
  buildHeaderToFieldMap,
  classifySheet,
  COLUMN_DEFINITIONS,
  findSheetName,
  headerKey,
  DATA_SYNC_SHEET_AREAS,
  DATA_SYNC_DOWNLOADED_IDS_SHEET,
  type ColumnDefinition,
  type DataAreaType
} from './excelConfig';
import {
  createMembershipRowSchema,
  createOrgFunctionRowSchema,
  createDataSyncUserSchema,
  createSystemRowSchema,
  type MembershipRowInput,
  type OrgFunctionRowInput,
  type DataSyncUserInput,
  type SystemRowInput,
  type UserInput
} from './excelSchemas';
import type { FieldError, ParsedRow } from './types';

export type ParsedOrgFunctionRow = ParsedRow<OrgFunctionRowInput>;
export type ParsedMembershipRow = ParsedRow<MembershipRowInput>;
export type ParsedSystemRow = ParsedRow<SystemRowInput>;
export type ParsedDataSyncUser = ParsedRow<DataSyncUserInput>;

type DataSyncUnitSheet = Exclude<DataSyncExcelSheet, 'people'>;

/** A sheet of the data source workbook: its name as the file has it, and its rows. */
interface DataSyncSheet<T> {
  name: string;
  rows: ParsedRow<T>[];
}

/**
 * The sheets of the Excel data source workbook; a structure sheet's rows
 * are the upload request's.
 */
type ParsedDataSyncSheets = {
  [S in DataSyncUnitSheet]: DataSyncSheet<StartExcelDataSyncRequest[S][number]>;
} & { people: DataSyncSheet<DataSyncUserInput> };

export interface OrgFunctionsParseResult {
  /** Rows from the main data sheet (functions or teams) */
  rows: ParsedOrgFunctionRow[];
  /** Rows from the memberships sheet */
  memberships: ParsedMembershipRow[];
}

/**
 * Normalize a row from Excel data using the header-to-field mapping
 * Converts translated headers to internal field names. Values are trimmed at
 * this boundary — Excel cells routinely carry stray whitespace that would
 * fail exact matching (`z.email()`, manager-email lookups) downstream. Cells
 * empty after trimming are omitted, so callers read fields via
 * `normalized.field || ''`.
 */
export function normalizeRow(
  row: Record<string, unknown>,
  headerToFieldMap: Map<string, string>
): Partial<Record<string, string>> {
  const normalized: Record<string, string> = {};

  for (const [header, value] of Object.entries(row)) {
    const field = fieldForHeader(header, headerToFieldMap);
    if (!field || value === undefined) continue;

    const trimmed = String(value).trim();
    if (trimmed !== '') {
      normalized[field] = trimmed;
    }
  }

  return normalized;
}

function fieldForHeader(header: unknown, headerToFieldMap: Map<string, string>) {
  return headerToFieldMap.get(headerKey(String(header)));
}

/** Turn a failed row validation into per-field errors the review table can show. */
export function extractErrors(error: z.ZodError): FieldError[] {
  return error.issues.map((issue) => ({ field: issue.path.join('.'), message: issue.message }));
}

/** A data row of a sheet: its cells by header, and its row number in the sheet. */
export interface SheetRow {
  cells: Record<string, unknown>;
  rowNumber: number;
}

export interface BuildRowsConfig<T> {
  schema: z.ZodType<T, unknown>;
  /** Map a normalized (header→field) row into the entity's row shape. */
  toData: (normalized: Partial<Record<string, string>>) => T;
  /** True for a blank row that should be dropped rather than validated. */
  isEmpty: (data: T) => boolean;
  /** Clean up the data kept on an INVALID row (e.g. drop a NaN before display). */
  sanitizeInvalid?: (data: T) => T;
  /** The message shown for an issue, where the schema's own is not for display. */
  issueMessage?: (issue: z.core.$ZodIssue) => string;
}

/**
 * The one map-normalize-skip-empty-validate loop behind every per-entity parser.
 * Callers supply only what differs: the schema, the row shape, and the
 * empty-row test. Exported so the per-entity builders — and their tests — share
 * exactly one implementation.
 */
export function buildRows<T>(
  sheetRows: SheetRow[],
  headerToFieldMap: Map<string, string>,
  config: BuildRowsConfig<T>
): ParsedRow<T>[] {
  const rows: ParsedRow<T>[] = [];

  sheetRows.forEach(({ cells, rowNumber }) => {
    const normalized = normalizeRow(cells, headerToFieldMap);
    const data = config.toData(normalized);

    if (config.isEmpty(data)) {
      return;
    }

    const result = config.schema.safeParse(data);
    if (result.success) {
      rows.push({ data: result.data, isValid: true, rowNumber });
    } else {
      rows.push({
        data: config.sanitizeInvalid ? config.sanitizeInvalid(data) : data,
        isValid: false,
        errors: config.issueMessage
          ? result.error.issues.map((issue) => ({
              field: issue.path.join('.'),
              message: config.issueMessage?.(issue) ?? issue.message
            }))
          : extractErrors(result.error),
        rowNumber
      });
    }
  });

  return rows;
}

/**
 * Map and validate raw sheet rows for a functions/teams main data sheet.
 * Exported separately from the file-reading shell so the logic is unit-testable.
 */
export function buildOrgFunctionRows(
  sheetRows: SheetRow[],
  headerToFieldMap: Map<string, string>
): ParsedOrgFunctionRow[] {
  return buildRows<OrgFunctionRowInput>(sheetRows, headerToFieldMap, {
    schema: createOrgFunctionRowSchema(),
    toData: (normalized) => ({
      name: normalized.name || '',
      description: normalized.description,
      department: normalized.department,
      leadEmail: normalized.leadEmail,
      email: normalized.email
    }),
    isEmpty: (data) =>
      !data.name && !data.description && !data.department && !data.leadEmail && !data.email
  });
}

/**
 * Map and validate raw sheet rows for a memberships sheet.
 * Exported separately from the file-reading shell so the logic is unit-testable.
 */
export function buildMembershipRows(
  sheetRows: SheetRow[],
  headerToFieldMap: Map<string, string>
): ParsedMembershipRow[] {
  return buildRows<MembershipRowInput>(sheetRows, headerToFieldMap, {
    schema: createMembershipRowSchema(),
    toData: (normalized) => ({
      orgFunctionName: normalized.orgFunctionName || '',
      memberEmail: normalized.memberEmail || ''
    }),
    isEmpty: (data) => !data.orgFunctionName && !data.memberEmail
  });
}

/** The user columns of a normalized row: the users and the data source uploads share them. */
export function toUserRowData(normalized: Partial<Record<string, string>>): UserInput {
  return {
    firstName: normalized.firstName || '',
    lastName: normalized.lastName || '',
    email: normalized.email || '',
    department: normalized.department || '',
    role: normalized.role || '',
    roleDescription: normalized.roleDescription
  };
}

/**
 * Map and validate raw rows of the Excel data source upload, keeping the
 * id column the user rows drop.
 */
export function buildDataSyncUserRows(
  sheetRows: SheetRow[],
  headerToFieldMap: Map<string, string>
): ParsedDataSyncUser[] {
  return buildRows<DataSyncUserInput>(sheetRows, headerToFieldMap, {
    schema: createDataSyncUserSchema(),
    toData: (normalized) => ({
      id: normalized.id,
      ...toUserRowData(normalized),
      location: normalized.location
    }),
    isEmpty: (data) => !data.id && !data.firstName && !data.lastName && !data.email
  });
}

/**
 * Parse the licence seats cell: Excel gives numbers or numeric strings; anything
 * non-numeric becomes NaN so the schema rejects it with a localized message.
 */
function parseLicenceSeats(raw: string | undefined): number | undefined {
  if (raw === undefined || raw.trim() === '') {
    return undefined;
  }
  return Number(raw.trim());
}

/**
 * Map and validate raw sheet rows for a systems data sheet.
 * Exported separately from the file-reading shell so the logic is unit-testable.
 */
export function buildSystemRows(
  sheetRows: SheetRow[],
  headerToFieldMap: Map<string, string>
): ParsedSystemRow[] {
  return buildRows<SystemRowInput>(sheetRows, headerToFieldMap, {
    schema: createSystemRowSchema(),
    toData: (normalized) => ({
      name: normalized.name || '',
      description: normalized.description,
      category: normalized.category,
      url: normalized.url,
      licenceSeats: parseLicenceSeats(normalized.licenceSeats),
      ownerEmail: normalized.ownerEmail || ''
    }),
    isEmpty: (data) =>
      !data.name &&
      !data.description &&
      !data.category &&
      !data.url &&
      data.licenceSeats === undefined &&
      !data.ownerEmail,
    // NaN would render as "NaN" in the review table; the error already says why
    sanitizeInvalid: (data) => ({
      ...data,
      licenceSeats: Number.isNaN(data.licenceSeats) ? undefined : data.licenceSeats
    })
  });
}

/**
 * Read an uploaded File into an XLSX workbook
 */
export async function readWorkbook(file: File): Promise<XLSX.WorkBook> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        resolve(XLSX.read(e.target?.result, { type: 'binary' }));
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsBinaryString(file);
  });
}

function sheetToRows(workbook: XLSX.WorkBook, sheetName: string | undefined): SheetRow[] {
  if (!sheetName) {
    return [];
  }
  return XLSX.utils
    .sheet_to_json<Record<string, unknown> & { __rowNum__: number }>(workbook.Sheets[sheetName])
    .map((cells) => ({
      cells,
      // sheet_to_json skips blank rows; `__rowNum__` is the row's 0-based sheet index.
      rowNumber: cells.__rowNum__ + 1
    }));
}

/**
 * Rows of the main data sheet. Unknown headers are dropped, so a sheet whose
 * headers all miss the template would parse to zero rows and pass as an empty
 * upload — it throws instead.
 */
export function readDataSheetRows(
  workbook: XLSX.WorkBook,
  headerToFieldMap: Map<string, string>
): SheetRow[] {
  const sheetName = findDataSheetName(workbook);
  if (!headerRow(workbook, sheetName).some((header) => fieldForHeader(header, headerToFieldMap))) {
    throw new ExcelHeaderMismatchError();
  }
  return sheetToRows(workbook, sheetName);
}

function headerRow(workbook: XLSX.WorkBook, sheetName: string | undefined): unknown[] {
  const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
  const ref = sheet?.['!ref'];
  if (!sheet || !ref) return [];
  const range = XLSX.utils.decode_range(ref);
  range.e.r = range.s.r;
  // Formatted text, the same form sheet_to_json gives normalizeRow as row keys.
  return XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, range, raw: false })[0] ?? [];
}

/** The fields the sheet's header row names. */
export function headerFields(
  workbook: XLSX.WorkBook,
  sheetName: string | undefined,
  headerToFieldMap: Map<string, string>
): Set<string | undefined> {
  return new Set(
    headerRow(workbook, sheetName).map((header) => fieldForHeader(header, headerToFieldMap))
  );
}

/**
 * Pick the main data sheet from a workbook. Anything that is neither the
 * instructions nor the memberships sheet counts as data (in any language), so
 * files with renamed data sheets still import; the first sheet is the fallback.
 */
export function findDataSheetName(workbook: XLSX.WorkBook): string | undefined {
  return (
    workbook.SheetNames.find((name) => classifySheet(name) === 'data') ?? workbook.SheetNames[0]
  );
}

/**
 * Parse a workbook for the functions or teams upload.
 * Reads the main data sheet plus the memberships sheet (both recognized in any
 * supported language). The kind only picks the header map — both templates
 * share the same row shape (ADR 0004: kind is fixed by the template, never a column).
 */
export function parseOrgFunctionsWorkbook(
  workbook: XLSX.WorkBook,
  kind: 'functions' | 'teams'
): OrgFunctionsParseResult {
  const mainHeaderMap = buildHeaderToFieldMap(kind);
  const membershipSheetName = workbook.SheetNames.find(
    (name) => classifySheet(name) === 'memberships'
  );
  const dataSheetName = workbook.SheetNames.find((name) => classifySheet(name) === 'data');
  // A memberships-only upload is valid, also next to an empty default sheet.
  const membershipsOnly =
    membershipSheetName !== undefined &&
    !headerRow(workbook, dataSheetName).some((header) => String(header).trim() !== '');

  return {
    rows: membershipsOnly
      ? []
      : buildOrgFunctionRows(readDataSheetRows(workbook, mainHeaderMap), mainHeaderMap),
    memberships: buildMembershipRows(
      sheetToRows(workbook, membershipSheetName),
      buildHeaderToFieldMap('memberships')
    )
  };
}

/** True for a row whose cells are all empty. */
const allEmpty = (data: object) => Object.values(data).every((value) => !value);

/**
 * A structure row's issues are a blank name or an invalid email; the
 * request schema's messages are not for display.
 */
const dataSyncUnitIssue = (issue: z.core.$ZodIssue) =>
  i18next.t(
    issue.path[0] === 'name'
      ? 'bulkOnboarding.validation.nameRequired'
      : 'bulkOnboarding.validation.invalidEmail'
  );

/**
 * The ids the download listed, from its hidden sheet: per sheet key in row 1,
 * the text cells below it, read cell by cell so only text counts as an id.
 * Undefined for a file without that sheet (a hand-built file or an older
 * download) or with a sheet whose row 1 names no sheet key (a damaged list).
 */
function readDownloadedIds(workbook: XLSX.WorkBook): DataSyncExcelDownloadedIds | undefined {
  const sheet = workbook.Sheets[DATA_SYNC_DOWNLOADED_IDS_SHEET];
  const ref = sheet?.['!ref'];
  if (!sheet || !ref) return undefined;
  const { e: end } = XLSX.utils.decode_range(ref);
  const textAt = (r: number, c: number) => {
    const cell: XLSX.CellObject | undefined = sheet[XLSX.utils.encode_cell({ r, c })];
    return typeof cell?.v === 'string' && cell.v !== '' ? cell.v : undefined;
  };
  const columns = DataSyncExcelSheetSchema.options.map((key) => ({
    key,
    column: Array.from({ length: end.c + 1 }, (_, c) => c).find((c) => textAt(0, c) === key)
  }));
  if (columns.every(({ column }) => column === undefined)) return undefined;
  const idsOf = (column: number | undefined) => {
    if (column === undefined) return [];
    return Array.from({ length: end.r }, (_, index) => textAt(index + 1, column)).filter(
      (id) => id !== undefined
    );
  };
  return DataSyncExcelDownloadedIdsSchema.parse(
    Object.fromEntries(columns.map(({ key, column }) => [key, idsOf(column)]))
  );
}

/**
 * Parse the Excel data source workbook: each sheet found by its name in
 * any supported language, its rows numbered as in the sheet. A workbook that
 * lacks sheets yields the data areas of the missing ones; one whose sheets
 * lack columns, those sheets and columns. Every column but an optional one
 * is compared, so a lost column must not read as a column of blank cells (a
 * blank parent means top level).
 */
export function parseDataSyncWorkbook(workbook: XLSX.WorkBook):
  | { kind: 'missingSheets'; dataAreas: DataAreaType[] }
  | { kind: 'missingColumns'; sheets: { name: string; columns: ColumnDefinition[] }[] }
  | {
      kind: 'parsed';
      sheets: ParsedDataSyncSheets;
      downloaded: DataSyncExcelDownloadedIds | undefined;
    } {
  const sheetNames = DataSyncExcelSheetSchema.options.map((sheet) => ({
    sheet,
    name: findSheetName(workbook.SheetNames, DATA_SYNC_SHEET_AREAS[sheet])
  }));
  const missingSheets = sheetNames
    .filter(({ name }) => name === undefined)
    .map(({ sheet }) => DATA_SYNC_SHEET_AREAS[sheet]);
  if (missingSheets.length > 0) return { kind: 'missingSheets', dataAreas: missingSheets };
  const nameOf = (sheet: DataSyncExcelSheet) =>
    sheetNames.find((found) => found.sheet === sheet)?.name ?? '';

  const missingColumns = DataSyncExcelSheetSchema.options.flatMap((sheet) => {
    const dataArea = DATA_SYNC_SHEET_AREAS[sheet];
    const fields = headerFields(workbook, nameOf(sheet), buildHeaderToFieldMap(dataArea));
    const columns = COLUMN_DEFINITIONS[dataArea].filter(
      ({ field, optional }) => !optional && !fields.has(field)
    );
    return columns.length > 0 ? [{ name: nameOf(sheet), columns }] : [];
  });
  if (missingColumns.length > 0) return { kind: 'missingColumns', sheets: missingColumns };

  const sheetRowsOf = (sheet: DataSyncExcelSheet) => ({
    sheetRows: sheetToRows(workbook, nameOf(sheet)),
    headerToFieldMap: buildHeaderToFieldMap(DATA_SYNC_SHEET_AREAS[sheet])
  });
  const unitSheet = <S extends DataSyncUnitSheet>(
    sheet: S,
    config: Pick<BuildRowsConfig<StartExcelDataSyncRequest[S][number]>, 'schema' | 'toData'>
  ) => {
    const { sheetRows, headerToFieldMap } = sheetRowsOf(sheet);
    return {
      name: nameOf(sheet),
      rows: buildRows(sheetRows, headerToFieldMap, {
        ...config,
        isEmpty: allEmpty,
        issueMessage: dataSyncUnitIssue
      })
    };
  };
  const { shape } = StartExcelDataSyncRequestSchema;
  // A blank cell is null in the request, as in the download.
  const orgFunctionSheet = (sheet: 'teams' | 'roles') =>
    unitSheet(sheet, {
      schema: shape[sheet].element,
      toData: ({ id, name, lead }) => ({ id, name: name ?? '', lead: lead ?? null })
    });
  const people = sheetRowsOf('people');
  return {
    kind: 'parsed',
    sheets: {
      departments: unitSheet('departments', {
        schema: shape.departments.element,
        toData: ({ id, name, parent, lead }) => ({
          id,
          name: name ?? '',
          parent: parent ?? null,
          lead: lead ?? null
        })
      }),
      teams: orgFunctionSheet('teams'),
      roles: orgFunctionSheet('roles'),
      systems: unitSheet('systems', {
        schema: shape.systems.element,
        toData: ({ id, name, owner }) => ({ id, name: name ?? '', owner: owner ?? null })
      }),
      people: {
        name: nameOf('people'),
        rows: buildDataSyncUserRows(people.sheetRows, people.headerToFieldMap)
      }
    },
    downloaded: readDownloadedIds(workbook)
  };
}

/**
 * Parse Excel file for the systems upload.
 * Accepts columns in any supported language.
 */
export async function parseSystemsFromExcel(file: File): Promise<ParsedSystemRow[]> {
  const workbook = await readWorkbook(file);

  const headerToFieldMap = buildHeaderToFieldMap('systems');
  return buildSystemRows(readDataSheetRows(workbook, headerToFieldMap), headerToFieldMap);
}
